import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import { useOrgStore } from '@/stores/orgStore';
import { getGroupedChildAssetIds } from './useAssetLinks';
import type { Asset, AssetFormData } from '@/types/models';

// Fetch all assets for an organization
export function useAssets(organizationId: string | undefined) {
  const hasHydrated = useOrgStore((state) => state.hasHydrated);

  return useQuery({
    queryKey: ['assets', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      const { data, error } = await supabase
        .from('assets')
        .select(
          `
          *,
          site:sites(id, name, address, city, state),
          location:locations(id, name, type),
          category:categories(id, name),
          department:departments(id, name),
          person_assets(
            id,
            assigned_at,
            person:people(id, full_name, email)
          )
        `
        )
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include assigned_to array
      return (data as any[]).map(asset => ({
        ...asset,
        assigned_to: asset.person_assets?.map((pa: any) => pa.person) || []
      })) as Asset[];
    },
    enabled: !!organizationId && hasHydrated,
  });
}

// Fetch a single asset
export function useAsset(assetId: string | undefined) {
  return useQuery({
    queryKey: ['assets', assetId],
    queryFn: async () => {
      if (!assetId) throw new Error('Asset ID is required');

      const { data, error } = await supabase
        .from('assets')
        .select(
          `
          *,
          site:sites(id, name, address, city, state),
          location:locations(id, name, type),
          category:categories(id, name),
          department:departments(id, name),
          person_assets(
            id,
            assigned_at,
            person:people(id, full_name, email)
          )
        `
        )
        .eq('id', assetId)
        .single();

      if (error) throw error;
      return data as Asset;
    },
    enabled: !!assetId,
  });
}

// Check if tag_id already exists in organization
export async function checkTagIdExists(
  organizationId: string,
  tagId: string,
  excludeAssetId?: string
): Promise<boolean> {
  let query = supabase
    .from('assets')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('tag_id', tagId);

  if (excludeAssetId) {
    query = query.neq('id', excludeAssetId);
  }

  const { data } = await query.limit(1);
  return (data?.length ?? 0) > 0;
}

// Create a new asset
export function useCreateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AssetFormData & { organization_id: string }) => {
      // Check for duplicate tag_id
      if (data.tag_id) {
        const exists = await checkTagIdExists(data.organization_id, data.tag_id);
        if (exists) {
          throw new Error(`Asset tag "${data.tag_id}" already exists in this organization. Please use a unique tag ID.`);
        }
      }

      const { error, data: newAsset } = await supabase
        .from('assets')
        .insert([
          {
            ...data,
            guid: crypto.randomUUID(), // Generate a GUID
          },
        ])
        .select()
        .single();

      if (error) {
        // Handle unique constraint violation with a user-friendly message
        if (error.code === '23505' && error.message.includes('tag_id')) {
          throw new Error(`Asset tag "${data.tag_id}" already exists. Please use a unique tag ID.`);
        }
        throw error;
      }
      return newAsset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      notifications.show({
        title: 'Success',
        message: 'Asset created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create asset',
        color: 'red',
      });
    },
  });
}

// Update an existing asset
export function useUpdateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data, organizationId }: { id: string; data: Partial<AssetFormData>; organizationId?: string }) => {
      // Check for duplicate tag_id if updating tag_id
      if (data.tag_id && organizationId) {
        const exists = await checkTagIdExists(organizationId, data.tag_id, id);
        if (exists) {
          throw new Error(`Asset tag "${data.tag_id}" already exists in this organization. Please use a unique tag ID.`);
        }
      }

      const { error, data: updatedAsset } = await supabase
        .from('assets')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        // Handle unique constraint violation with a user-friendly message
        if (error.code === '23505' && error.message.includes('tag_id')) {
          throw new Error(`Asset tag "${data.tag_id}" already exists. Please use a unique tag ID.`);
        }
        throw error;
      }
      return updatedAsset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      notifications.show({
        title: 'Success',
        message: 'Asset updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update asset',
        color: 'red',
      });
    },
  });
}

// Delete an asset
export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('assets').delete().eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      notifications.show({
        title: 'Success',
        message: 'Asset deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete asset',
        color: 'red',
      });
    },
  });
}

// Assign asset to person (with cascade to grouped child assets)
export function useAssignAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assetId,
      personId,
      organizationId,
    }: {
      assetId: string;
      personId: string;
      organizationId: string;
    }) => {
      // Get grouped child asset IDs
      const groupedChildIds = await getGroupedChildAssetIds(organizationId, assetId);
      const allAssetIds = [assetId, ...groupedChildIds];

      // Update all assets' status to Assigned
      const { error: assetError } = await supabase
        .from('assets')
        .update({ status: 'Assigned' })
        .in('id', allAssetIds);

      if (assetError) throw assetError;

      // Create person_asset relationships for all assets
      const assignments = allAssetIds.map((id) => ({
        organization_id: organizationId,
        person_id: personId,
        asset_id: id,
        assigned_at: new Date().toISOString(),
      }));

      const { error: assignError } = await supabase
        .from('person_assets')
        .upsert(assignments, { onConflict: 'person_id,asset_id' });

      if (assignError) throw assignError;

      return { assignedCount: allAssetIds.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      const message = data.assignedCount > 1
        ? `${data.assignedCount} assets assigned successfully (including linked assets)`
        : 'Asset assigned successfully';
      notifications.show({
        title: 'Success',
        message,
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to assign asset',
        color: 'red',
      });
    },
  });
}

// Unassign asset from person (with cascade to grouped child assets)
export function useUnassignAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assetId,
      organizationId,
      newStatus = 'Available',
    }: {
      assetId: string;
      organizationId: string;
      newStatus?: 'Available' | 'Maintenance' | 'Retired' | 'Lost';
    }) => {
      // Get grouped child asset IDs
      const groupedChildIds = await getGroupedChildAssetIds(organizationId, assetId);
      const allAssetIds = [assetId, ...groupedChildIds];

      // Delete person_asset relationships for all assets
      const { error: deleteError } = await supabase
        .from('person_assets')
        .delete()
        .in('asset_id', allAssetIds);

      if (deleteError) throw deleteError;

      // Update all assets' status to the selected status
      const { error: assetError } = await supabase
        .from('assets')
        .update({ status: newStatus })
        .in('id', allAssetIds);

      if (assetError) throw assetError;

      return { unassignedCount: allAssetIds.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      const message = data.unassignedCount > 1
        ? `${data.unassignedCount} assets unassigned successfully (including linked assets)`
        : 'Asset unassigned successfully';
      notifications.show({
        title: 'Success',
        message,
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to unassign asset',
        color: 'red',
      });
    },
  });
}
